import { Component, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { HttpClient } from "@angular/common/http";
import { CalculatorInputs, CalculatorResults } from "./calculators.interface";

@Component({
  selector: "app-calculators",
  templateUrl: "./calculators.component.html",
  styleUrls: ["./calculators.component.scss"],
  standalone: false,
})
export class CalculatorsComponent implements OnInit {
  inputs: CalculatorInputs = {
    currency: "PLN",
    totalEmployees: 100,
    itAdminHourlyRate: 250,
    weeklyHoursSpentOnSignatures: 0,
    averageMonthlyRevenuePerCustomer: 1500,
    websiteConversionRate: 3,
    leadToCustomerConversionRate: 25,
    ctr: 1,
    monthlyEmailsSent: 0,
    averageEmailsPerEmployee: 420,
    maintenanceTimePercentage: 15,
    email: "",
    termsAccepted: false,
  };

  isSending = false;
  showSuccessMessage = false;
  showErrorMessage = false;

  results: CalculatorResults = {
    gSignatureCost: 0,
    monthlyAdminCost: 0,
    monthlyLeadsFromClicks: 0,
    monthlyClicksFromSignatures: 0,
    monthlyNewCustomers: 0,
    monthlyAdminSavings: 0,
    monthlyRevenueFromCustomers: 0,
    timeSavedPercentage: 0,
    monthlyTotalProfit: 0,
    roiItAdmin: 0,
    roiMarketing: 0,
    totalRoi: 0,
  };

  constructor(
    public translate: TranslateService,
    private http: HttpClient,
  ) {}

  ngOnInit(): void {
    this.initializeLanguage();
    this.loadSettingsFromUrl();
    this.calculate();
  }

  private initializeLanguage(): void {
    const browserLang = this.translate.getBrowserLang();
    const supportedLangs = ["pl", "en", "es"];
    const defaultLang = supportedLangs.includes(browserLang)
      ? browserLang
      : "en";
    this.translate.use(defaultLang);
  }

  changeLanguage(lang: string): void {
    this.translate.use(lang);
  }

  calculate(): void {
    const pricePerEmployee =
      this.inputs.currency === "EUR" || this.inputs.currency === "USD" ? 2 : 8;
    this.results.gSignatureCost = this.inputs.totalEmployees * pricePerEmployee;

    this.inputs.weeklyHoursSpentOnSignatures =
      (1.186 * this.inputs.totalEmployees) / 100;

    this.inputs.monthlyEmailsSent =
      this.inputs.averageEmailsPerEmployee * this.inputs.totalEmployees;

    this.results.monthlyAdminCost =
      this.inputs.itAdminHourlyRate *
      this.inputs.weeklyHoursSpentOnSignatures *
      4.345;

    this.results.monthlyClicksFromSignatures =
      (this.inputs.ctr / 100) * this.inputs.monthlyEmailsSent;

    this.results.monthlyLeadsFromClicks =
      (this.inputs.websiteConversionRate / 100) *
      this.results.monthlyClicksFromSignatures;

    this.results.monthlyNewCustomers =
      (this.inputs.leadToCustomerConversionRate / 100) *
      this.results.monthlyLeadsFromClicks;

    this.results.monthlyAdminSavings =
      this.results.monthlyAdminCost *
        (1 - this.inputs.maintenanceTimePercentage / 100) -
      this.results.gSignatureCost;

    this.results.monthlyRevenueFromCustomers =
      this.inputs.averageMonthlyRevenuePerCustomer *
      this.results.monthlyNewCustomers;

    this.results.timeSavedPercentage =
      (1 - this.inputs.maintenanceTimePercentage / 100) * 100;

    this.results.monthlyTotalProfit =
      this.results.monthlyAdminSavings +
      this.results.monthlyRevenueFromCustomers;

    this.results.roiItAdmin =
      (this.results.monthlyAdminSavings / this.results.gSignatureCost) * 100;

    this.results.roiMarketing =
      ((this.results.monthlyRevenueFromCustomers -
        this.results.gSignatureCost) /
        this.results.gSignatureCost) *
      100;

    this.results.totalRoi =
      (this.results.roiMarketing + this.results.roiItAdmin) / 2;
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat("pl-PL", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  formatNumberWithDecimals(value: number, decimals: number = 2): string {
    return new Intl.NumberFormat("pl-PL", {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    }).format(value);
  }

  formatPercentage(value: number): string {
    return (value * 100).toFixed(1) + "%";
  }

  getTermsOfServiceUrl(): string {
    const currentLang = this.translate.currentLang;
    const baseUrl = "https://gsignature.com";

    switch (currentLang) {
      case "es":
        return `${baseUrl}/es/terms-of-service`;
      case "pl":
        return `${baseUrl}/pl/terms-of-service`;
      case "en":
      default:
        return `${baseUrl}/terms-of-service`;
    }
  }

  isEmailValid(): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(this.inputs.email);
  }

  isFormValid(): boolean {
    return this.isEmailValid() && this.inputs.termsAccepted;
  }

  getEmailErrorClass(): string {
    return this.inputs.email && !this.isEmailValid() ? "error" : "";
  }

  getTermsErrorClass(): string {
    return !this.inputs.termsAccepted ? "error" : "";
  }

  async sendCalculatorLink(): Promise<void> {
    if (!this.isFormValid()) {
      return;
    }

    this.isSending = true;
    this.showSuccessMessage = false;
    this.showErrorMessage = false;

    try {
      await this.http
        .post("/api/calculator/send-link", {
          email: this.inputs.email,
          settings: this.inputs,
          language: this.translate.currentLang,
        })
        .toPromise();

      this.showSuccessMessage = true;
      this.showErrorMessage = false;
    } catch {
      this.showSuccessMessage = false;
      this.showErrorMessage = true;
    } finally {
      this.isSending = false;
    }
  }

  private loadSettingsFromUrl(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const settingsId = urlParams.get("settings");

    if (settingsId) {
      void this.loadSettings(settingsId);
    }
  }

  private async loadSettings(settingsId: string): Promise<void> {
    const response = (await this.http
      .get(`/api/calculator/settings/${settingsId}`)
      .toPromise()) as { settings: CalculatorInputs };
    if (response && response.settings) {
      this.inputs = { ...this.inputs, ...response.settings };
      this.calculate();
    }
  }
}
